import moment from 'moment'

import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { DATE_FORMAT } from '../../constants'
import { commonStrings, oaCreationStrings } from 'constants/strings'
import {
  EOaCreationJobStatus,
  type TOaCreationJobListItem,
} from 'types'

type JobsHistoryTableProps = {
  jobs: TOaCreationJobListItem[]
  loading: boolean
}

const statusBadgeClass: Record<EOaCreationJobStatus, string> = {
  [EOaCreationJobStatus.PENDING]: 'bg-surface text-muted',
  [EOaCreationJobStatus.RUNNING]: 'bg-accent-soft text-accent-strong',
  [EOaCreationJobStatus.PAUSED]: 'bg-amber-100 text-amber-800',
  [EOaCreationJobStatus.COMPLETED]: 'bg-emerald-100 text-emerald-700',
  [EOaCreationJobStatus.FAILED]: 'bg-danger/15 text-danger',
  [EOaCreationJobStatus.ROLLED_BACK]: 'bg-amber-100 text-amber-700',
  [EOaCreationJobStatus.ROLLBACK_TODO]: 'bg-amber-100 text-amber-700',
  [EOaCreationJobStatus.CANCELLED]: 'bg-surface text-muted',
}

export function JobsHistoryTable({
  jobs,
  loading,
}: JobsHistoryTableProps) {
  const formatProgress = (job: TOaCreationJobListItem): string => {
    const total = job.quantity
    if (job.failedCount > 0) {
      return oaCreationStrings.table.progressWithFailedTemplate
        .replace('$success', String(job.successCount))
        .replace('$failed', String(job.failedCount))
        .replace('$total', String(total))
    }
    return oaCreationStrings.table.progressTemplate
      .replace('$success', String(job.successCount))
      .replace('$total', String(total))
  }

  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = <DataTableMessageRow colSpan={7} message={commonStrings.loading} />
  } else if (jobs.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={7}
        message={oaCreationStrings.table.states.empty}
      />
    )
  } else {
    body = jobs.map((job) => (
      <JobsHistoryTableRow
        key={job.id}
        job={job}
        formatProgress={formatProgress}
      />
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1000px]"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.createdAt}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.accountName}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.icon}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.quantity}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.progress}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.email}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {oaCreationStrings.table.headers.status}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

type JobsHistoryTableRowProps = {
  job: TOaCreationJobListItem
  formatProgress: (job: TOaCreationJobListItem) => string
}

function JobsHistoryTableRow({
  job,
  formatProgress,
}: JobsHistoryTableRowProps) {
  return (
    <tr className="border-t border-border/60 bg-white transition-colors hover:bg-neutral-50">
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {moment(job.createdAt).format(`${DATE_FORMAT} HH:mm`)}
      </td>
      <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
        {job.accountName}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {job.iconImageUrl ? (
          <img
            src={job.iconImageUrl}
            alt={job.accountName}
            className="h-10 w-10 rounded-full border border-border/80 object-cover"
          />
        ) : (
          <span className="text-muted">
            {oaCreationStrings.table.states.noIcon}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {job.quantity}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {formatProgress(job)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm break-all">
        {job.email}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="flex flex-col gap-1">
          <span
            className={`inline-block w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass[job.status]}`}
          >
            {oaCreationStrings.status[job.status]}
          </span>
          {job.pauseReason && (
            <span
              className="text-[10px] text-amber-800 break-words sm:text-[11px]"
              title={job.pauseReason}
            >
              <span className="font-semibold">
                {oaCreationStrings.table.pauseReasonLabel}:
              </span>{' '}
              {job.pauseReason}
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}
